<template>
	<div>
		<!-- Inputs -->
		<p class="mb4 fs3 fw7 c6">{{ $t("excel_inputs") }}</p>

		<div v-for="(item, index) in inputs" :key="index" class="mb4">
			<p class="mb1 fs3 c6">{{ item.var }}</p>
			<div class="flex xs-betwween xs-middle">
				<div
					@click="handleInputSelect(index)"
					class="input-value xs r2 pl2 pr2 pt3 pb3 cu2 b1"
					:class="
						item.isNotExist ? 'bcError' : item.isNotAdded ? 'bcWarning' : 'bc6'
					"
				>
					<span>{{ item.liquid || "---" }}</span>
				</div>

				<div
					v-if="item.isNotExist"
					class="ml2 c6 cu2"
					@click="removeInput(item.id)"
				>
					x
				</div>
			</div>
			<div v-if="item.isNotAdded" class="fs3 fw7 mt1 cWarning">
				{{ $t("excel_input_not_added") }}
			</div>
			<div v-if="item.isNotExist" class="fs3 fw7 mt1 cError">
				{{ $t("excel_input_not_exist") }}
			</div>
		</div>

		<!-- Empty state -->
		<div v-if="!inputs" class="xs r2 pl2 pr2 pt3 pb3 cu2 b1 bc6">
			<span class="c6 fs3">{{ $t("errors.no_data") }}</span>
		</div>

		<!-- Outputs -->
		<p class="mt6 mb4 fs3 fw7 c6">{{ $t("excel_outputs") }}</p>

		<div v-for="(item, index) in outputs" :key="index" class="mb4">
			<p class="mb1 fs3 c6">{{ item.var }}</p>
			<div class="flex xs-betwween xs-middle">
				<div
					@click="handleOutputSelect(index)"
					class="input-value xs r2 pl2 pr2 pt3 pb3 cu2 b1"
					:class="
						item.isNotExist ? 'bcError' : item.isNotAdded ? 'bcWarning' : 'bc6'
					"
				>
					<span>{{ item.liquid || "---" }}</span>
				</div>
				<div
					v-if="item.isNotExist"
					class="ml2 c6 cu2"
					@click="removeOutput(item.id)"
				>
					x
				</div>
			</div>
			<div v-if="item.isNotAdded" class="fs3 fw7 mt1 cWarning">
				{{ $t("excel_output_not_added") }}
			</div>
			<div v-if="item.isNotExist" class="fs3 fw7 mt1 cError">
				{{ $t("excel_output_not_exist") }}
			</div>
		</div>

		<!-- Empty state -->
		<div v-if="!outputs" class="xs r2 pl2 pr2 pt3 pb3 cu2 b1 bc6">
			<span class="c6 fs3">{{ $t("errors.no_data") }}</span>
		</div>

		<core-modal
			name="treeSelect"
			:headline="$t('select_data')"
			:pivotX="0"
			:pivotY="0"
			:hasHeader="false"
		>
			<div class="bg5 p4" style="height:100vh; overflow: scroll;">
				<!-- <liquid-builder /> -->
				<liquid-data-picker @data:selected="afterDataSelect" />
			</div>
		</core-modal>
	</div>
</template>

<script>
import axios from "axios"
import XLSX from "xlsx"
import CoreModal from "@/modules/core/components/CoreModal"

import { getDataSchemaFromXlsx } from "./xlsxUtils.js"
import LiquidDataPicker from "../LiquidDataPicker/"

import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		LiquidDataPicker,
		CoreModal
	},

	props: {
		payload: {
			type: Object
		}
	},

	data() {
		return {
			fileData: null,
			fileKey: null,
			workBookData: null,
			inputPrefix: "input_",
			outputPrefix: "output_",
			namedRangeInputs: [], // generated inputs from downloaded and parsed excel file
			namedRangeOutputs: [], // generated inputs from downloaded and parsed excel file
			inputs: null,
			outputs: null,
			selectedInput: null,
			selectedOutput: null
		}
	},

	methods: {
		async downloadExcelFile() {
			if (!this.fileKey) return

			const res = await axios({
				url: `https://api.asia.covergo.com/api/v1/files/${encodeURIComponent(
					this.fileKey
				)}`,
				headers: {
					Authorization: `Bearer ${window.TK}`
				},
				method: "GET",
				responseType: "blob" // important
			})

			const reader = new FileReader()
			reader.readAsArrayBuffer(res.data)
			reader.onload = () => {
				const data = new Uint8Array(reader.result)
				this.workBookData = XLSX.read(data, { type: "array" })
				this.namedRangeInputs = getDataSchemaFromXlsx({
					wb: this.workBookData,
					prefix: this.inputPrefix
				})
				this.namedRangeOutputs = getDataSchemaFromXlsx({
					wb: this.workBookData,
					prefix: this.outputPrefix
				})
			}
		},

		compareInputsAndParsedInputs() {
			const namedRangeInputsArray = this.namedRangeInputs?.map(
				item => item.label
			)
			const templateInputsArray = this.inputs?.map(item => item.var)

			// namedRanged parsed from excel which are not yet added to template inputs
			const itemsNotAdded = namedRangeInputsArray?.filter(
				item => !templateInputsArray?.includes(item)
			)

			// inputs that exist on template input but not on namedRanges inputs pasrsed from excel
			const itemsNotExist = templateInputsArray?.filter(
				item => !namedRangeInputsArray?.includes(item)
			)

			// add isNotExist boolean which will add red border to those inputs that do not exist on excel and should be deleted
			this.inputs = this.inputs?.map(item => {
				if (itemsNotExist.includes(item.var)) {
					return { ...item, isNotExist: true }
				} else return item
			})

			if (this?.inputs?.length > 0) {
				// add isNotAdded boolean which will add green border to those inputs that exist on excel but have not yet added to template
				this.inputs = [
					...this.inputs,
					...itemsNotAdded?.map(item => ({
						var: item,
						liquid: null,
						isNotAdded: true
					}))
				]
			} else
				this.inputs = [
					...itemsNotAdded?.map(item => ({
						var: item,
						liquid: null,
						isNotAdded: true
					}))
				]
		},

		compareOutputsAndParsedOutputs() {
			const namedRangeOutputsArray = this.namedRangeOutputs?.map(
				item => item.label
			)
			const templateOutputsArray = this.outputs?.map(item => item.var)

			// namedRanged parsed from excel which are not yet added to template inputs
			const itemsNotAdded = namedRangeOutputsArray?.filter(
				item => !templateOutputsArray?.includes(item)
			)

			// inputs that exist on template input but not on namedRanges inputs pasrsed from excel
			const itemsNotExist = templateOutputsArray?.filter(
				item => !namedRangeOutputsArray?.includes(item)
			)

			// add isNotExist boolean which will add red border to those inputs that do not exist on excel and should be deleted
			this.outputs = this.outputs?.map(item => {
				if (itemsNotExist.includes(item.var)) {
					return { ...item, isNotExist: true }
				} else return item
			})

			if (this?.outputs?.length > 0) {
				// add isNotAdded boolean which will add green border to those inputs that exist on excel but have not yet added to template
				this.outputs = [
					...this.outputs,
					...itemsNotAdded?.map(item => ({
						var: item,
						isNotAdded: true
					}))
				]
			} else
				this.outputs = [
					...itemsNotAdded?.map(item => ({
						var: item,
						isNotAdded: true
					}))
				]
		},

		handleInputSelect(index) {
			this.selectedInput = index
			this.$modal.show("treeSelect")
		},

		handleOutputSelect(index) {
			this.selectedOutput = index

			//TODO: We don't have liquid on outputs yet. so we will just add or update output
			// this.$modal.show("treeSelect")

			const foundOutput = this.outputs[this.selectedOutput]

			if (foundOutput?.isNotExist) return

			if (foundOutput?.isNotAdded) {
				this.addOutput(foundOutput)
			} else this.updateOutput(foundOutput)
		},

		afterDataSelect(value) {
			this.$modal.hide("treeSelect")
			const foundInput = this.inputs[this.selectedInput]
			foundInput.liquid = value

			if (foundInput?.isNotExist) return

			if (foundInput?.isNotAdded) {
				this.addInput(foundInput)
			} else this.updateInput(foundInput)
		},

		async addInput(payload) {
			const query = gql`
				mutation addInputToFunctionTemplate(
					$id: String!
					$input: functionInputInput!
				) {
					addInputToFunctionTemplate(id: $id, input: $input) {
						status
						errors
						createdStatus {
							id
						}
					}
				}
			`

			const input = {
				name: payload?.var?.replace(this.inputPrefix, ""),
				var: payload?.var,
				liquid: payload?.liquid
			}

			const variables = {
				id: this.$route.params.id,
				input
			}

			const res = await api({ query, variables })

			if (res?.data?.addInputToFunctionTemplate?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.addInputToFunctionTemplate?.errors ||
							res?.data?.addInputToFunctionTemplate?.errors_2?.message ||
							"Oops ! An error occured."
					}
				})
			}

			this.$modal.hide("addInput")
			this.inputs[this.selectedInput] = {
				id: res?.data?.addInputToFunctionTemplate?.createdStatus?.id,
				...input
			}
			this.compareInputsAndParsedInputs()
		},

		async removeInput(id) {
			const query = gql`
				mutation removeInputFromFunctionTemplate(
					$templateId: String!
					$inputId: String!
				) {
					removeInputFromFunctionTemplate(
						templateId: $templateId
						inputId: $inputId
					) {
						status
						errors
					}
				}
			`
			const variables = {
				templateId: this.$route.params.id,
				inputId: id
			}

			const res = await api({ query, variables })

			if (res?.data?.removeInputFromFunctionTemplate?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.removeInputFromFunctionTemplate?.errors ||
							res?.data?.removeInputFromFunctionTemplate?.errors_2?.message ||
							"Oops ! An error occured."
					}
				})
			} else
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("input_removed")
					}
				})

			this.inputs = this.inputs?.filter(item => item.id !== id)
		},

		async updateInput(payload) {
			const query = gql`
				mutation updateInputOfFunctionTemplate(
					$templateId: String!
					$inputId: String!
					$input: functionInputInput!
				) {
					updateInputOfFunctionTemplate(
						templateId: $templateId
						inputId: $inputId
						input: $input
					) {
						status
						errors
					}
				}
			`

			const input = {
				name: payload?.var?.replace(this.inputPrefix, ""),
				var: payload?.var,
				liquid: payload?.liquid
			}

			const variables = {
				templateId: this.$route.params.id,
				inputId: payload.id,
				input
			}

			const res = await api({ query, variables })

			if (res?.data?.updateInputOfFunctionTemplate?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.updateInputOfFunctionTemplate?.errors ||
							res?.data?.updateInputOfFunctionTemplate?.errors_2?.message ||
							"Oops ! An error occured."
					}
				})
			}

			this.inputs[this.selectedInput] = {
				id: payload?.id,
				...input
			}
		},

		async addOutput(payload) {
			const query = gql`
				mutation addOutputToFunctionTemplate(
					$id: String!
					$input: functionOutputInput!
				) {
					addOutputToFunctionTemplate(id: $id, input: $input) {
						status
						errors
						createdStatus {
							id
						}
					}
				}
			`
			const input = {
				name: payload?.var?.replace(this.outputPrefix, ""),
				var: payload?.var
			}

			const variables = {
				id: this.$route.params.id,
				input
			}

			const res = await api({ query, variables })

			if (res?.data?.addOutputToFunctionTemplate?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.addOutputToFunctionTemplate?.errors ||
							res?.data?.addOutputToFunctionTemplate?.errors_2?.message ||
							"Oops ! An error occured."
					}
				})
			}

			this.outputs[this.selectedOutput] = {
				id: res?.data?.addOutputToFunctionTemplate?.createdStatus?.id,
				...input
			}
			this.compareOutputsAndParsedOutputs()
		},

		async removeOutput(id) {
			const query = gql`
				mutation removeOutputFromFunctionTemplate(
					$templateId: String!
					$outputId: String!
				) {
					removeOutputFromFunctionTemplate(
						templateId: $templateId
						outputId: $outputId
					) {
						status
						errors
					}
				}
			`
			const variables = {
				templateId: this.$route.params.id,
				outputId: id
			}

			const res = await api({ query, variables })

			if (res?.data?.removeOutputFromFunctionTemplate?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.removeOutputFromFunctionTemplate?.errors ||
							res?.data?.removeOutputFromFunctionTemplate?.errors_2?.message ||
							"Oops ! An error occured."
					}
				})
			} else
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("output_removed")
					}
				})

			this.outputs = this.outputs?.filter(item => item.id !== id)
		},

		async updateOutput(payload) {
			const query = gql`
				mutation updateOutputOfFunctionTemplate(
					$templateId: String!
					$outputId: String!
					$input: functionInputInput!
				) {
					updateOutputOfFunctionTemplate(
						templateId: $templateId
						outputId: $outputId
						input: $input
					) {
						status
						errors
					}
				}
			`

			const input = {
				name: payload?.var?.replace(this.outputPrefix, ""),
				var: payload?.var
			}
			const variables = {
				templateId: this.$route.params.id,
				outputId: payload.id,
				input
			}

			const res = await api({ query, variables })

			if (res?.data?.updateOutputOfFunctionTemplate?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.updateOutputOfFunctionTemplate?.errors ||
							res?.data?.updateOutputOfFunctionTemplate?.errors_2?.message ||
							"Oops ! An error occured."
					}
				})
			}

			this.outputs[this.selectedOutput] = {
				id: payload?.id,
				...input
			}
		}
	},

	computed: {
		inputOptions() {
			return this.namedRangeInputs?.map(item => ({
				name: item?.label,
				value: item?.label
			}))
		}
	},

	watch: {
		"payload.fileKey": {
			async handler() {
				this.fileKey = await this.payload?.fileKey
				this.downloadExcelFile()
			},
			immediate: true,
			deep: true
		},
		"payload.inputs": {
			async handler() {
				this.inputs = this.payload?.inputs
			},
			immediate: true,
			deep: true
		},
		"payload.outputs": {
			async handler() {
				this.outputs = this.payload?.outputs
			},
			immediate: true,
			deep: true
		},
		namedRangeInputs: {
			handler() {
				this.compareInputsAndParsedInputs()
			}
		},
		namedRangeOutputs: {
			handler() {
				this.compareOutputsAndParsedOutputs()
			}
		}
	}
}
</script>

<style scoped>
.input-value:hover {
	background-color: rgba(0, 0, 0, 0.06);
}
</style>
